// Requests from a warehouse by quantity (CR_20260915_Request_Qty): the row shapes of request_overview and pure rules
// shared by the web and the phone — line remainders, which picked asset counts against which line, the progress
// line and whose move it is. The server (fulfil_request) enforces the same rules; these only guide the screens.
import type { AssetOverview } from './types';

export type RequestStatus =
  'submitted' | 'approved' | 'rejected' | 'cancelled' | 'expired' | 'closed';
export type RequestPriority = 'normal' | 'urgent';
export type NextActor = 'manager' | 'warehouse' | 'requester' | null;

/**
 * Why a manager who is not one of the project's named approvers decided it anyway (CR_20260919 D6). Chosen from a
 * fixed list rather than typed: a reason written one-handed in a yard becomes "ok", and an audit trail that looks
 * complete while carrying nothing is worse than none. `other` requires the note.
 */
export type CoverReason = 'on_leave' | 'unreachable' | 'urgent' | 'requester' | 'other';
export const COVER_REASONS: readonly CoverReason[] = [
  'on_leave',
  'unreachable',
  'urgent',
  'requester',
  'other',
];

/** A project's own approval rule; null follows the company default (CR_20260919 D1). */
export type ProjectApproval = 'required' | 'none' | null;

/**
 * Does a request for this project need approving? The mirror of `app.request_approval_required(company, project)`:
 * the project's own rule wins, null inherits the company default (D1). Shared by the web and the phone so the two
 * clients cannot drift from the server — they did, which is `G-MG-1` / `G-PU-1`.
 *
 * Note what is *not* here: the caller's role. D7 removed the `role = 'manager'` term, so a manager's own request
 * waits like anyone else's and nobody approves their own.
 */
export function approvalRequired(
  companyDefaultOn: boolean,
  projectRule: ProjectApproval = null,
): boolean {
  return projectRule ? projectRule === 'required' : companyDefaultOn;
}

/**
 * Must the requester name the warehouse? Only when nothing needs approving — then the request goes straight there,
 * so `submit_request` demands a source ("choose the warehouse that will supply it", 22023). When approval *is*
 * required the warehouse is the approver's choice and naming one is optional.
 */
export function sourceRequiredOnSubmit(
  companyDefaultOn: boolean,
  projectRule: ProjectApproval = null,
): boolean {
  return !approvalRequired(companyDefaultOn, projectRule);
}

export interface RequestLine {
  category_id: string;
  category_name: string | null;
  spec: string | null;
  qty_requested: number;
  qty_approved: number | null;
  qty_dispatched: number;
  qty_received: number;
}

/** A row of public.request_overview: never any warehouse stock (Q5). */
export interface RequestOverview {
  request_id: string;
  company_id: string;
  ref: string;
  status: RequestStatus;
  priority: RequestPriority;
  needed_by: string;
  project_id: string;
  project_name: string | null;
  to_site_id: string;
  to_site_name: string | null;
  source_site_id: string | null;
  preferred_source_site_id: string | null;
  requested_by: string;
  requested_for: string | null;
  note: string | null;
  decided_by: string | null;
  decided_at: string | null;
  decision_note: string | null;
  /** decided by a manager who is not one of the project's named approvers (CR_20260919 D2) */
  decided_as_cover?: boolean;
  cover_reason?: CoverReason | null;
  returned_at: string | null;
  returned_note: string | null;
  short_at: string | null;
  closed_at: string | null;
  closed_reason: 'delivered' | 'short' | null;
  close_note: string | null;
  created_at: string;
  updated_at: string;
  lines: RequestLine[];
  qty_requested: number;
  qty_approved: number;
  qty_dispatched: number;
  qty_received: number;
  qty_remaining: number;
  is_late: boolean;
  next_actor: NextActor;
  returned_by: string | null;
  short_by: string | null;
}

/** A row of request_availability(): managers and warehouse staff only. */
export interface RequestAvailability {
  site_id: string;
  site_name: string;
  category_id: string;
  available: number;
  committed: number;
}

/** Still to send on a line: approved minus dispatched, never below 0; nothing once closed short. */
export function lineRemainder(line: RequestLine, closedShort = false): number {
  if (closedShort) return 0;
  return Math.max((line.qty_approved ?? 0) - line.qty_dispatched, 0);
}

/**
 * Which picked assets fit the request (Q7): an asset counts against the line of its category, up to that line's
 * remainder. Returns the ids that fit, and per category how many are picked and left.
 */
export function fitPicks(
  lines: readonly RequestLine[],
  picked: readonly { asset_id: string; category_id: string | null }[],
  closedShort = false,
): {
  fits: string[];
  refused: { asset_id: string; reason: 'no_line' | 'over' }[];
  perLine: Map<string, { picked: number; left: number }>;
} {
  const left = new Map(lines.map((l) => [l.category_id, lineRemainder(l, closedShort)]));
  const perLine = new Map(
    lines.map((l) => [l.category_id, { picked: 0, left: left.get(l.category_id)! }]),
  );
  const fits: string[] = [];
  const refused: { asset_id: string; reason: 'no_line' | 'over' }[] = [];
  for (const a of picked) {
    const slot = a.category_id ? perLine.get(a.category_id) : undefined;
    if (!slot) refused.push({ asset_id: a.asset_id, reason: 'no_line' });
    else if (slot.left <= 0) refused.push({ asset_id: a.asset_id, reason: 'over' });
    else {
      slot.picked += 1;
      slot.left -= 1;
      fits.push(a.asset_id);
    }
  }
  return { fits, refused, perLine };
}

/** "6 × Steel plate · 1 × Boring bucket": approved quantities once decided (0 left out), else requested. */
export function linesText(r: Pick<RequestOverview, 'status' | 'lines'>): string {
  const decided = r.status === 'approved' || r.status === 'closed';
  return r.lines
    .map((l) => ({
      qty: decided ? (l.qty_approved ?? 0) : l.qty_requested,
      name: l.category_name ?? '—',
    }))
    .filter((l) => l.qty > 0)
    .map((l) => `${l.qty} × ${l.name}`)
    .join(' · ');
}

/** The bucket a request sits in on the list: waiting approval · to prepare · in progress · closed (Q16). */
export type RequestBucket = 'waiting' | 'to_prepare' | 'in_progress' | 'closed';
export function requestBucket(r: Pick<RequestOverview, 'status' | 'next_actor'>): RequestBucket {
  if (r.status === 'submitted') return 'waiting';
  if (r.status === 'approved') return r.next_actor === 'warehouse' ? 'to_prepare' : 'in_progress';
  return 'closed';
}

/** Past its needed-by date (a company-timezone calendar date, YYYY-MM-DD) while still open. */
export function isLate(
  r: Pick<RequestOverview, 'status' | 'needed_by' | 'is_late'>,
  today: string,
): boolean {
  if (r.status === 'approved') return r.is_late;
  return r.status === 'submitted' && r.needed_by < today;
}

/** Free to pick at the warehouse (Q7): active, in service (CR_20260921 L15 — request_availability does not count an
 * out-of-service asset either), in its custody and on site there (or never heard of), not on an open transfer, and of
 * a requested category. */
export function pickable(
  a: Pick<
    AssetOverview,
    | 'custody_site_id'
    | 'asset_status'
    | 'open_transfer_id'
    | 'category_id'
    | 'presence_state'
    | 'presence_site_id'
  > &
    Partial<Pick<AssetOverview, 'service_state'>>,
  source: string,
  categories: ReadonlySet<string>,
): boolean {
  return (
    a.custody_site_id === source &&
    a.asset_status === 'active' &&
    a.service_state !== 'out_of_service' &&
    !a.open_transfer_id &&
    !!a.category_id &&
    categories.has(a.category_id) &&
    (!a.presence_state ||
      a.presence_state === 'unknown' ||
      (a.presence_state === 'on_site' && a.presence_site_id === source))
  );
}

/**
 * Picking by BLE (R3): the free assets whose tag the phone hears, strongest signal first — a tag of another category,
 * or an asset that is not free here, is never offered.
 */
export function heardPickable<T extends { asset_id: string }>(
  heard: readonly { rssi: number; tag?: { asset_id: string | null } | null }[],
  candidates: readonly T[],
): T[] {
  const byId = new Map(candidates.map((c) => [c.asset_id, c]));
  const out: T[] = [];
  for (const h of [...heard].sort((a, b) => b.rssi - a.rssi)) {
    const c = h.tag?.asset_id ? byId.get(h.tag.asset_id) : undefined;
    if (c && !out.includes(c)) out.push(c);
  }
  return out;
}

/**
 * The "from" side of a request, for every screen that shows where it comes from (owner, 2026-09-27: a bare "—" under
 * Warehouse read as missing data). `chosen`: the source the manager approved it from. `preferred`: the requester's
 * wish, not yet decided — always shown as such. `pending`: waiting for approval with no wish; the manager picks
 * (CR_20260915 Q6). `none`: decided without a source (rejected, cancelled, expired).
 */
export type RequestSource =
  | { kind: 'chosen'; siteId: string }
  | { kind: 'preferred'; siteId: string }
  | { kind: 'pending' }
  | { kind: 'none' };
export function requestSource(
  r: Pick<RequestOverview, 'status' | 'source_site_id' | 'preferred_source_site_id'>,
): RequestSource {
  if (r.source_site_id) return { kind: 'chosen', siteId: r.source_site_id };
  if (r.status !== 'submitted') return { kind: 'none' };
  if (r.preferred_source_site_id) return { kind: 'preferred', siteId: r.preferred_source_site_id };
  return { kind: 'pending' };
}
